import { Injectable } from '@angular/core';
import { Observable, of, delay, throwError } from 'rxjs';
import {
    IncentiveTravel,
    IncentiveTravelRequirement,
    IncentiveTravelPoint,
    IncentiveTravelPointRequest,
    IncentiveTravelPointsResponse,
    RappelPlanType,
    Provider
} from '../models';
import { ProductCategoryEnum } from 'src/app/core/enums/product-category.enum';

@Injectable({
    providedIn: 'root'
})
export class MockDataService {

    // Mock data storage
    private mockTravels: IncentiveTravel[] = [
        {
            id: 1,
            title: 'Viaje a París 2025',
            description: 'Viaje de incentivos a París para talleres destacados',
            destination: 'París, Francia',
            generalConditions: 'Condiciones aplicables para talleres con rendimiento excepcional',
            isActive: true,
            objectivePoints: 15000,
            pointsEarned: 12500,
            startDate: new Date('2025-03-01'),
            endDate: new Date('2025-06-30'),
            filesDto: [
                { incentiveTravelId: '1', fileType: 0, fileName: 'itinerario-paris.pdf', externalUrl: 'mock-url', fileBase64: 'mock-base64' },
                { incentiveTravelId: '1', fileType: 0, fileName: 'condiciones.pdf', externalUrl: 'mock-url', fileBase64: 'mock-base64' }
            ]
        },
        {
            id: 2,
            title: 'Escapada a Barcelona',
            description: 'Weekend de incentivos en Barcelona',
            destination: 'Barcelona, España',
            generalConditions: 'Viaje de fin de semana para talleres de nivel medio',
            isActive: true,
            objectivePoints: 8000,
            pointsEarned: 5600,
            startDate: new Date('2025-04-15'),
            endDate: new Date('2025-08-15'),
            filesDto: []
        },
        {
            id: 3,
            title: 'Tour Roma Histórica',
            description: 'Viaje cultural a Roma',
            destination: 'Roma, Italia',
            generalConditions: 'Experiencia cultural para talleres veteranos',
            isActive: false,
            objectivePoints: 20000,
            pointsEarned: 18900,
            startDate: new Date('2024-10-01'),
            endDate: new Date('2024-12-31'),
            filesDto: [
                { incentiveTravelId: '3', fileType: 0, fileName: 'guia-roma.pdf', externalUrl: 'mock-url', fileBase64: 'mock-base64' }
            ]
        }
    ];

    private mockRequirements: IncentiveTravelRequirement[] = [
        {
            id: 1,
            providerId: 1,
            productCategoryId: ProductCategoryEnum.TIRES,
            minimumQuantity: 10,
            brand: 'Michelin',
            centerGroupId: 100,
            incentiveTravelId: '1'
        },
        {
            id: 2,
            providerId: 2,
            productCategoryId: ProductCategoryEnum.TIRES,
            minimumQuantity: 5,
            brand: 'Bosch',
            centerGroupId: 100,
            incentiveTravelId: '1'
        },
        {
            id: 3,
            providerId: 1,
            productCategoryId: ProductCategoryEnum.TIRES,
            minimumQuantity: 15,
            brand: 'Continental',
            centerGroupId: 100,
            incentiveTravelId: '2'
        }
    ];

    private mockProviders: Provider[] = [
        {
            id: 1,
            name: 'Proveedor Neumáticos S.L.',
            connectionType: 1,
            email: 'info@neumaticos.com',
            address: 'Calle Principal 123, Madrid',
            fiscalType: 1,
            fiscalAddress: 'Calle Principal 123, Madrid',
            centerId: 1,
            centerName: 'Centro Madrid',
            lastStockImportDate: '2025-01-15',
            contact: {
                id: 1,
                fullName: 'Juan García',
                phone: '+34 600 123 456',
                email: 'juan.garcia@neumaticos.com'
            }
        },
        {
            id: 2,
            name: 'Repuestos Automoción',
            connectionType: 2,
            email: 'ventas@repuestos.com',
            address: 'Avenida Industrial 45, Barcelona',
            fiscalType: 1,
            fiscalAddress: 'Avenida Industrial 45, Barcelona',
            centerId: 2,
            centerName: 'Centro Barcelona',
            lastStockImportDate: '2025-01-10',
            contact: {
                id: 2,
                fullName: 'María López',
                phone: '+34 600 789 012',
                email: 'maria.lopez@repuestos.com'
            }
        },
        {
            id: 3,
            name: 'Lubricantes Industriales',
            connectionType: 1,
            email: 'contacto@lubricantes.com',
            address: 'Polígono Norte 78, Valencia',
            fiscalType: 2,
            fiscalAddress: 'Polígono Norte 78, Valencia',
            centerId: 3,
            centerName: 'Centro Valencia',
            lastStockImportDate: '2025-01-08',
            contact: {
                id: 3,
                fullName: 'Carlos Ruiz',
                phone: '+34 600 345 678',
                email: 'carlos.ruiz@lubricantes.com'
            }
        }
    ];

    private mockCategories = [
        { id: 'CAT001', name: 'Neumáticos' },
        { id: 'CAT002', name: 'Frenos' },
        { id: 'CAT003', name: 'Lubricantes' },
        { id: 'CAT004', name: 'Filtros' },
        { id: 'CAT005', name: 'Baterías' }
    ];

    private mockBrands = [
        'Michelin', 'Continental', 'Bridgestone', 'Pirelli',
        'Bosch', 'Brembo', 'ATE', 'TRW',
        'Castrol', 'Shell', 'Mobil', 'Total',
        'Mann-Filter', 'Mahle', 'Fram',
        'Varta', 'Exide', 'Banner'
    ];

    private mockPoints: IncentiveTravelPoint[] = [
        {
            id: 1,
            providerId: 1,
            brand: 'Michelin',
            minimumQuantity: 4,
            points: 150,
            rappelPlan: RappelPlanType.ALL,
            acceptedMeasures: [],
            centerGroupId: 100,
            createdAt: new Date('2025-01-01'),
            updatedAt: new Date('2025-01-15')
        },
        {
            id: 2,
            providerId: 1,
            brand: 'Continental',
            minimumQuantity: 6,
            points: 200,
            rappelPlan: RappelPlanType.POINT_S,
            acceptedMeasures: [],
            centerGroupId: 100,
            createdAt: new Date('2025-01-05'),
            updatedAt: new Date('2025-01-10')
        },
        {
            id: 3,
            providerId: 2,
            brand: 'Bridgestone',
            minimumQuantity: 4,
            points: 175,
            rappelPlan: RappelPlanType.POINT_S_EXPRESS,
            acceptedMeasures: [],
            centerGroupId: 100,
            createdAt: new Date('2025-01-08'),
            updatedAt: new Date('2025-01-12')
        }
    ];

    constructor() { }

    // Incentive Travel methods
    getIncentiveTravels(): Observable<IncentiveTravel[]> {
        console.log('🟡 MockDataService: Getting travels from local storage');
        return of([...this.mockTravels]).pipe(delay(300));
    }

    createIncentiveTravel(travelData: IncentiveTravel): Observable<IncentiveTravel> {
        console.log('🟡 MockDataService: Creating travel locally', travelData);
        const newTravel = {
            ...travelData,
            id: Math.max(...this.mockTravels.map(t => t.id || 0)) + 1
        };
        this.mockTravels.push(newTravel);
        return of(newTravel).pipe(delay(500));
    }

    updateIncentiveTravel(travelId: number, travelData: IncentiveTravel): Observable<IncentiveTravel> {
        console.log('🟡 MockDataService: Updating travel locally', travelId, travelData);
        const index = this.mockTravels.findIndex(t => t.id === travelId);
        if (index !== -1) {
            this.mockTravels[index] = { ...travelData, id: travelId };
            return of(this.mockTravels[index]).pipe(delay(500));
        }
        return throwError(() => new Error('Travel not found'));
    }

    deleteIncentiveTravel(travelId: number): Observable<void> {
        console.log('🟡 MockDataService: Deleting travel locally', travelId);
        const index = this.mockTravels.findIndex(t => t.id === travelId);
        if (index !== -1) {
            this.mockTravels.splice(index, 1);
            // También eliminar requisitos asociados
            this.mockRequirements = this.mockRequirements.filter(r => r.incentiveTravelId !== travelId.toString());
            return of(void 0).pipe(delay(300));
        }
        return throwError(() => new Error('Travel not found'));
    }

    // Requirements methods
    getIncentiveTravelRequirements(travelId: string): Observable<IncentiveTravelRequirement[]> {
        console.log('🟡 MockDataService: Getting requirements for travel', travelId);
        const requirements = this.mockRequirements.filter(r => r.incentiveTravelId === travelId);
        return of([...requirements]).pipe(delay(300));
    }

    createIncentiveTravelRequirement(travelId: string, requirementData: IncentiveTravelRequirement): Observable<IncentiveTravelRequirement> {
        console.log('🟡 MockDataService: Creating requirement locally', travelId, requirementData);
        const newRequirement = {
            ...requirementData,
            id: Math.max(...this.mockRequirements.map(r => r.id || 0)) + 1,
            incentiveTravelId: travelId
        };
        this.mockRequirements.push(newRequirement);
        return of(newRequirement).pipe(delay(500));
    }

    updateIncentiveTravelRequirement(requirementId: number, requirementData: IncentiveTravelRequirement): Observable<IncentiveTravelRequirement> {
        console.log('🟡 MockDataService: Updating requirement locally', requirementId, requirementData);
        const index = this.mockRequirements.findIndex(r => r.id === requirementId);
        if (index !== -1) {
            this.mockRequirements[index] = { ...requirementData, id: requirementId };
            return of(this.mockRequirements[index]).pipe(delay(500));
        }
        return throwError(() => new Error('Requirement not found'));
    }

    deleteIncentiveTravelRequirement(requirementId: number): Observable<void> {
        console.log('🟡 MockDataService: Deleting requirement locally', requirementId);
        const index = this.mockRequirements.findIndex(r => r.id === requirementId);
        if (index !== -1) {
            this.mockRequirements.splice(index, 1);
            return of(void 0).pipe(delay(300));
        }
        return throwError(() => new Error('Requirement not found'));
    }

    // Providers methods
    getProviders(): Observable<Provider[]> {
        console.log('🟡 MockDataService: Getting providers from local storage');
        return of([...this.mockProviders]).pipe(delay(200));
    }

    // Categories methods (simulated)
    getCategories(): Observable<any[]> {
        console.log('🟡 MockDataService: Getting categories from local storage');
        return of([...this.mockCategories]).pipe(delay(200));
    }

    // Brands methods (simulated)
    getBrands(): Observable<string[]> {
        console.log('🟡 MockDataService: Getting brands from local storage');
        return of([...this.mockBrands]).pipe(delay(200));
    }

    // Points methods
    getIncentiveTravelPoints(campaignId: number): Observable<IncentiveTravelPointsResponse> {
        console.log('🟡 MockDataService: Getting points for campaign', campaignId);
        // Filter points by campaign (in real implementation, this would be done by the backend)
        const filteredPoints = this.mockPoints.filter(() => true); // For now, return all points

        const response: IncentiveTravelPointsResponse = {
            points: [...filteredPoints],
            total: filteredPoints.length,
            page: 1,
            limit: 50
        };

        return of(response).pipe(delay(300));
    }

    createIncentiveTravelPoint(campaignId: number, pointData: IncentiveTravelPointRequest): Observable<IncentiveTravelPoint> {
        const newPoint: IncentiveTravelPoint = {
            id: this.getNextPointId(),
            ...pointData,
            centerGroupId: 100,
            createdAt: new Date(),
            updatedAt: new Date()
        };

        this.mockPoints.push(newPoint);

        return of(newPoint).pipe(delay(500));
    }

    updateIncentiveTravelPoint(pointId: number, pointData: IncentiveTravelPointRequest): Observable<IncentiveTravelPoint> {
        const index = this.mockPoints.findIndex(p => p.id === pointId);
        if (index === -1) {
            console.error('Point not found:', pointId);
            return throwError(() => new Error('Point not found'));
        }

        const updatedPoint: IncentiveTravelPoint = {
            ...this.mockPoints[index],
            ...pointData,
            updatedAt: new Date()
        };

        this.mockPoints[index] = updatedPoint;
        return of(updatedPoint).pipe(delay(500));
    }

    deleteIncentiveTravelPoint(pointId: number): Observable<void> {
        const index = this.mockPoints.findIndex(p => p.id === pointId);
        if (index === -1) {
            console.error('Point not found:', pointId);
            return throwError(() => new Error('Point not found'));
        }

        this.mockPoints.splice(index, 1);

        return of(void 0).pipe(delay(300));
    }

    private getNextPointId(): number {
        return this.mockPoints.length > 0 ? Math.max(...this.mockPoints.map(p => p.id)) + 1 : 1;
    }

    // Debug methods
    getAllMockData() {
        return {
            travels: this.mockTravels,
            requirements: this.mockRequirements,
            points: this.mockPoints,
            providers: this.mockProviders,
            categories: this.mockCategories,
            brands: this.mockBrands
        };
    }

    resetMockData() {
        console.log('🔄 MockDataService: Resetting all mock data');
        // Restaurar datos iniciales si es necesario
        location.reload();
    }
}
